import { useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import Tooltip from "./Tooltip";
import type { TooltipPlacement, TooltipTheme } from "./Tooltip";
import { combineClasses } from "@/lib/utils";
import toast from "@/lib/toast";
import styles from "./TableCellTooltip.module.scss";

export interface TableCellTooltipProps {
  /** The value rendered in the cell itself. */
  value?: string | number | null;
  /** Tooltip content, if it should differ from `value` (e.g. richer JSX). */
  tooltipContent?: ReactNode;
  /** Only shows a tooltip once the cell's own text is actually truncated. */
  onlyIfTruncated?: boolean;
  placement?: TooltipPlacement;
  theme?: TooltipTheme;
  /** Adds a copy-to-clipboard button inside the tooltip. */
  copyable?: boolean;
  /** 1 = single-line ellipsis (default); >1 = multi-line clamp. */
  maxLines?: number;
  className?: string | undefined;
  textClassName?: string | undefined;
  maxWidth?: number | string;
  maxHeight?: number | string;
  interactive?: boolean;
  /** Text alignment inside the cell (the span fills the cell's width). */
  align?: "left" | "center" | "right";
  /**
   * Tags the rendered span `data-tag="allowRowEvents"` — react-data-table
   * only fires `onRowClicked` when the click target itself carries it.
   */
  allowRowEvents?: boolean;
}

// The common "table cell that might be too long for its column" case,
// built on `Tooltip`: a single-line ellipsis (or multi-line clamp) span
// that only shows its tooltip once truncation actually kicks in. See
// pages/Support/Support.tsx's Question/Answer columns for a worked
// example — this replaces what used to be a plain `title=""` attribute
// there (functional, but no rich content, slow native browser styling,
// and no copy button).
const TableCellTooltip = ({
  value,
  tooltipContent,
  onlyIfTruncated = true,
  placement = "top",
  theme = "dark",
  copyable = false,
  maxLines = 1,
  className,
  textClassName,
  maxWidth = 320,
  maxHeight = 180,
  interactive = true,
  align,
  allowRowEvents = false,
}: TableCellTooltipProps) => {
  const [copied, setCopied] = useState(false);
  const dataTag = allowRowEvents ? "allowRowEvents" : undefined;

  if (value === null || value === undefined || value === "") {
    return (
      <span className={styles.emptyCell} data-tag={dataTag}>
        —
      </span>
    );
  }

  const textValue = String(value);

  const handleCopy = (event: MouseEvent) => {
    event.stopPropagation();
    if (!navigator.clipboard?.writeText) {
      toast.error("Clipboard isn't available in this browser.");
      return;
    }
    navigator.clipboard
      .writeText(textValue)
      .then(() => {
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => toast.error("Failed to copy"));
  };

  const content = (
    <div className={styles.content}>
      <span className={styles.contentText}>{tooltipContent ?? textValue}</span>
      {copyable && (
        <button type="button" className={styles.copyButton} onClick={handleCopy}>
          {copied ? "Copied" : "Copy"}
        </button>
      )}
    </div>
  );

  return (
    <Tooltip
      content={content}
      placement={placement}
      theme={theme}
      onlyIfTruncated={onlyIfTruncated}
      interactive={interactive}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      className={className}
    >
      {/* The clamp/ellipsis CSS has to land on this exact element — `Tooltip`
          measures scrollWidth/scrollHeight on whatever it's given, and a
          truncated child nested a level deeper wouldn't register as overflow
          on its own untruncated parent. `tabIndex` makes a plain `<span>`
          keyboard-focusable at all (MUI's own docs note this — a non-
          interactive HTML element needs it explicitly to receive focus
          events); left unconditional rather than gated on truncation state
          for simplicity, since `onlyIfTruncated` already keeps an untruncated
          cell's tooltip from opening even if it is focused. */}
      <span
        tabIndex={0}
        className={combineClasses(maxLines > 1 ? styles.clampText : styles.singleLineText, textClassName)}
        style={{
          ...(maxLines > 1 && { WebkitLineClamp: maxLines }),
          ...(align && { textAlign: align }),
        }}
        data-tag={dataTag}
      >
        {textValue}
      </span>
    </Tooltip>
  );
};

export default TableCellTooltip;
